import { ReactNode, useEffect, useLayoutEffect, useRef } from "react"
import { useMsal } from "@azure/msal-react"
import { clearPreviewCache } from "@/utils/previewCache"
import { PreviewAccountContext } from "@/utils/previewAccount"
import { clearCadAssetCache } from "@/utils/cadAssetCache"

type Props = { children: ReactNode }

export function PreviewSessionBoundary({ children }: Props) {
    const { instance, accounts = [] } = useMsal()
    const account = instance.getActiveAccount() ?? accounts[0]
    const accountKey = account?.homeAccountId || account?.localAccountId || null
    const previousAccount = useRef(accountKey)

    // Clear before child passive effects start requests for a newly selected account.
    useLayoutEffect(() => {
        if (previousAccount.current !== null && previousAccount.current !== accountKey) {
            clearPreviewCache()
            clearCadAssetCache()
        }
        previousAccount.current = accountKey
        // `accounts` is included so MSAL account/logout events trigger this
        // boundary even when the active-account object is unchanged.
    }, [accountKey, accounts])

    useEffect(
        () => () => {
            if (previousAccount.current !== null) {
                clearPreviewCache()
                clearCadAssetCache()
            }
        },
        []
    )

    return (
        <PreviewAccountContext.Provider value={accountKey ?? "signed-out"}>{children}</PreviewAccountContext.Provider>
    )
}
